import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  BadgeOutlined as BadgeOutlinedIcon,
  BusinessOutlined as BusinessOutlinedIcon,
  CalendarTodayOutlined as CalendarIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  DragIndicator as DragIndicatorIcon,
  DrawOutlined as DrawOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  ExpandMore as ExpandMoreIcon,
  HomeOutlined as HomeOutlinedIcon,
  KeyOutlined as KeyOutlinedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
  MonetizationOnOutlined as MonetizationOnOutlinedIcon,
  NumbersOutlined as NumbersOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  PhoneOutlined as PhoneOutlinedIcon,
  StarOutline as StarOutlineIcon,
  Tune as TuneIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
} from "@mui/icons-material";
import { FavouriteDragItem } from "./FavouriteDragAndDrop";

const FAVOURITES_STORAGE_KEY = "pdf-editor-field-favourites-v1";
const FAVOURITE_CATEGORIES_STORAGE_KEY = "pdf-editor-favourite-categories-v1";

const FAVOURITE_CATEGORIES = [
  { key: "project", label: "Project", color: "#64748b" },
  { key: "owner", label: "Owner", color: "#2563eb" },
  { key: "contractor", label: "Contractor", color: "#d97706" },
  { key: "ownerSignature", label: "Owner signature", color: "#2563eb" },
  {
    key: "contractorSignature",
    label: "Contractor signature",
    color: "#d97706",
  },
  {
    key: "ownerNotarization",
    label: "Owner notarization",
    color: "#2563eb",
  },
  {
    key: "contractorNotarization",
    label: "Contractor notarization",
    color: "#d97706",
  },
  { key: "other", label: "Other", color: "#64748b" },
];

const CATEGORY_COLOR_OPTIONS = [
  { label: "Slate", value: "#64748b" },
  { label: "Blue", value: "#2563eb" },
  { label: "Orange", value: "#d97706" },
];

const FAVOURITE_ICON_OPTIONS = {
  description: {
    label: "Document",
    icon: <DescriptionOutlinedIcon fontSize="small" />,
  },
  number: { label: "Number", icon: <NumbersOutlinedIcon fontSize="small" /> },
  location: {
    label: "Location",
    icon: <LocationOnOutlinedIcon fontSize="small" />,
  },
  person: { label: "Person", icon: <PersonOutlineIcon fontSize="small" /> },
  business: {
    label: "Business",
    icon: <BusinessOutlinedIcon fontSize="small" />,
  },
  home: { label: "Property", icon: <HomeOutlinedIcon fontSize="small" /> },
  calendar: { label: "Date", icon: <CalendarIcon fontSize="small" /> },
  tag: { label: "Tag", icon: <LocalOfferOutlinedIcon fontSize="small" /> },
  money: {
    label: "Money",
    icon: <MonetizationOnOutlinedIcon fontSize="small" />,
  },
  phone: { label: "Phone", icon: <PhoneOutlinedIcon fontSize="small" /> },
  email: { label: "Email", icon: <EmailOutlinedIcon fontSize="small" /> },
  badge: {
    label: "ID / License",
    icon: <BadgeOutlinedIcon fontSize="small" />,
  },
  key: { label: "Key", icon: <KeyOutlinedIcon fontSize="small" /> },
  signature: {
    label: "Signature",
    icon: <DrawOutlinedIcon fontSize="small" />,
  },
  seal: {
    label: "Notary seal",
    icon: <VerifiedOutlinedIcon fontSize="small" />,
  },
};

const DEFAULT_FAVOURITE_TEMPLATES = [
  { category: "project", terms: ["permit number"], icon: "number" },
  { category: "project", terms: ["project address"], icon: "location" },
  {
    category: "project",
    terms: ["parcel_id", "Tax Folio/Parcel ID"],
    icon: "tag",
  },
  { category: "owner", terms: ["owner name"], icon: "person" },
  { category: "owner", terms: ["owner phone"], icon: "phone" },
  { category: "owner", terms: ["owner email"], icon: "email" },
  { category: "owner", terms: ["owner_full_address"], icon: "home" },
  {
    category: "contractor",
    terms: ["contractor_company_name"],
    icon: "business",
  },
  { category: "contractor", terms: ["contractor_full_name"], icon: "person" },

  { category: "contractor", terms: ["contractor phone"], icon: "phone" },
  { category: "contractor", terms: ["contractor_full_address"], icon: "home" },
  { category: "contractor", terms: ["contractor email"], icon: "email" },
  { category: "ownerSignature", terms: ["owner signature"], icon: "signature" },
  {
    category: "ownerSignature",
    terms: ["Owner Signature Full Address"],
    icon: "home",
  },

  {
    category: "contractorSignature",
    terms: ["contractor signature"],
    icon: "signature",
  },
  {
    category: "contractorSignature",
    terms: ["contractor_sign_address"],
    icon: "home",
  },
  {
    category: "ownerNotarization",
    terms: ["owner notary seal", "owner notarization seal"],
    icon: "seal",
  },
  {
    category: "ownerNotarization",
    terms: ["owner notary signature"],
    icon: "signature",
  },
  {
    category: "contractorNotarization",
    terms: ["contractor notary seal", "contractor notarization seal"],
    icon: "seal",
  },
  {
    category: "contractorNotarization",
    terms: ["contractor notary signature"],
    icon: "signature",
  },
];

const normalizeText = (value) =>
  String(value ?? "")
    .replace(/[’']/g, "'")
    .replace(/'s\b/gi, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const getFieldId = (field) =>
  field?.field_id ?? field?.id ?? field?.field_name ?? field?.name;

const getFieldLabel = (field) =>
  field?.field_label ??
  field?.label ??
  field?.field_name ??
  field?.name ??
  "Field";

export const flattenFavouriteFields = (fields = []) => {
  const fieldsById = new Map();
  const parentById = new Map();

  const visit = (items, parent = null, isOption = false) => {
    if (!Array.isArray(items)) return;

    items.forEach((item) => {
      if (!item || typeof item !== "object") return;

      const fieldId = isOption
        ? (item.field_id ?? item.value ?? item.id)
        : getFieldId(item);

      const parentId =
        item.parent_field_id ?? (parent ? getFieldId(parent) : null);

      const normalizedField = {
        ...item,
        field_id: fieldId,
        field_label:
          item.field_label || item.label || item.field_name || item.name || "",
        location: item.location ?? parent?.location ?? "create",
      };

      if (fieldId !== undefined && fieldId !== null && fieldId !== "") {
        const key = String(fieldId);

        if (!fieldsById.has(key)) {
          fieldsById.set(key, normalizedField);
        }

        // Capture parent information even for a duplicate option.
        if (
          parentId !== undefined &&
          parentId !== null &&
          parentId !== "" &&
          String(parentId) !== key
        ) {
          parentById.set(key, String(parentId));
        }
      }

      visit(item.children, normalizedField);
      visit(item.options, normalizedField, true);
    });
  };

  visit(fields);

  // Resolve parents after collecting the complete field list.
  return [...fieldsById.entries()].map(([key, field]) => {
    const parentId = parentById.get(key);
    const parent = parentId ? fieldsById.get(parentId) : null;

    return {
      ...field,
      ...(parentId ? { parent_field_id: parentId } : {}),
      favourite_parent_label: parent ? getFieldLabel(parent) : "",
    };
  });
};

const createDefaultFavourites = (fields) => {
  const usedFieldIds = new Set();

  return DEFAULT_FAVOURITE_TEMPLATES.reduce((favourites, template, index) => {
    const matchingFields = fields.filter((field) => {
      const fieldId = getFieldId(field);
      return fieldId && !usedFieldIds.has(fieldId);
    });

    const terms = template.terms.map(normalizeText);

    // Prefer an exact label/name match.
    const matchedField =
      matchingFields.find((field) =>
        [getFieldLabel(field), field?.field_name]
          .filter(Boolean)
          .some((label) => terms.includes(normalizeText(label))),
      ) ||
      matchingFields.find((field) => {
        const searchableText = normalizeText(
          `${getFieldLabel(field)} ${field?.field_name ?? ""} ${
            field?.category_label ?? ""
          }`,
        );

        return terms.some((term) => searchableText.includes(term));
      });

    if (!matchedField) return favourites;

    const fieldId = getFieldId(matchedField);
    usedFieldIds.add(fieldId);
    favourites.push({
      id: `default-${template.category}-${fieldId}-${index}`,
      fieldId,
      category: template.category,
      label: getFieldLabel(matchedField),
      icon: template.icon,
    });
    return favourites;
  }, []);
};

const getAutomaticCategories = (signatures = {}) => {
  const categories = [];

  ["owner", "contractor"].forEach((person) => {
    const selection = signatures?.[person];

    if (selection?.sign === true) {
      categories.push(`${person}Signature`);
    }

    if (selection?.notary === true) {
      categories.push(`${person}Notarization`);
    }
  });

  return categories;
};

const readStoredFavourites = () => {
  try {
    if (typeof window === "undefined") return null;

    const raw = window.localStorage.getItem(FAVOURITES_STORAGE_KEY);
    if (raw === null) return null;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const seenIds = new Set();

    return parsed.filter((item) => {
      if (
        !item ||
        typeof item.id !== "string" ||
        !["string", "number"].includes(typeof item.fieldId) ||
        typeof item.label !== "string" ||
        typeof item.category !== "string" ||
        !item.category.trim() ||
        seenIds.has(item.id)
      ) {
        return false;
      }

      seenIds.add(item.id);
      return true;
    });
  } catch {
    return null;
  }
};

const readStoredFavouriteCategories = () => {
  try {
    const raw = window.localStorage.getItem(FAVOURITE_CATEGORIES_STORAGE_KEY);

    if (!raw) return FAVOURITE_CATEGORIES;

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return FAVOURITE_CATEGORIES;

    const seen = new Set();

    const validCategories = parsed.filter((category) => {
      if (
        !category ||
        typeof category.key !== "string" ||
        !category.key.trim() ||
        typeof category.label !== "string" ||
        !category.label.trim() ||
        seen.has(category.key)
      ) {
        return false;
      }

      seen.add(category.key);
      return true;
    });

    // Keep Other available as the destination for removed categories.
    if (!seen.has("other")) {
      validCategories.push(
        FAVOURITE_CATEGORIES.find((category) => category.key === "other"),
      );
    }

    return validCategories;
  } catch {
    return FAVOURITE_CATEGORIES;
  }
};

const getFavouriteOptionLabel = (field) => {
  const label = getFieldLabel(field);

  return field?.favourite_parent_label
    ? `${label} (${field.favourite_parent_label})`
    : label;
};

const FavouritesBar = ({
  fields = [],
  onFavouriteSelect,
  signerContext,
  loading = false,
  currentSession,
  onNotify,
}) => {
  const [categories, setCategories] = useState(FAVOURITE_CATEGORIES);

  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#64748b");

  const availableFields = useMemo(
    () => flattenFavouriteFields(fields),
    [fields],
  );
  const [expanded, setExpanded] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draggedFavouriteId, setDraggedFavouriteId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(
    () => new Set(["project", "owner", "contractor", "other"]),
  );
  const [draftFavourite, setDraftFavourite] = useState({
    fieldId: "",
    category: "project",
    label: "",
    icon: "description",
  });
  const [favourites, setFavourites] = useState([]);
  const [preferencesHydrated, setPreferencesHydrated] = useState(false);
  const initializedRef = useRef(false);
  const serverPreferencesExistRef = useRef(false);
  const favouritesRef = useRef([]);
  const categoriesRef = useRef(FAVOURITE_CATEGORIES);
  const saveQueueRef = useRef(Promise.resolve());
  const onNotifyRef = useRef(onNotify);

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const autocompleteInputRef = useRef(null);

  const automaticCategories = useMemo(
    () =>
      getAutomaticCategories(signerContext).filter((key) =>
        categories.some((category) => category.key === key),
      ),
    [signerContext, categories],
  );

  const automaticCategoryKey = automaticCategories.join("|");

  const previousAutomaticCategoriesRef = useRef(new Set());

  useEffect(() => {
    favouritesRef.current = favourites;
  }, [favourites]);

  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  useEffect(() => {
    onNotifyRef.current = onNotify;
  }, [onNotify]);

  const persistPreferences = useCallback(
    (nextFavourites, nextCategories, onPersisted) => {
      if (!currentSession) return Promise.resolve();

      saveQueueRef.current = saveQueueRef.current
        .catch(() => undefined)
        .then(() =>
          axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-favourites/save`,
            {
              currentSession,
              favourites: nextFavourites,
              categories: nextCategories,
            },
            { withCredentials: true },
          ),
        )
        .then((response) => {
          onPersisted?.(response);
          return response;
        })
        .catch(() => {
          onNotifyRef.current?.(
            "error",
            "Your favourites could not be saved. Please try again.",
          );
        });

      return saveQueueRef.current;
    },
    [currentSession],
  );

  const persistFavouriteDeletion = useCallback(
    (favouriteId) => {
      if (!currentSession) return Promise.resolve();

      saveQueueRef.current = saveQueueRef.current
        .catch(() => undefined)
        .then(() =>
          axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-favourites/delete`,
            { currentSession, favouriteId },
            { withCredentials: true },
          ),
        )
        .catch(() => {
          onNotifyRef.current?.(
            "error",
            "Your favourite could not be deleted. Please try again.",
          );
        });

      return saveQueueRef.current;
    },
    [currentSession],
  );

  const commitPreferences = useCallback(
    (nextFavourites, nextCategories) => {
      favouritesRef.current = nextFavourites;
      categoriesRef.current = nextCategories;
      setFavourites(nextFavourites);
      setCategories(nextCategories);
      persistPreferences(nextFavourites, nextCategories);
    },
    [persistPreferences],
  );

  useEffect(() => {
    if (!currentSession) return undefined;

    let active = true;
    initializedRef.current = false;
    serverPreferencesExistRef.current = false;
    setPreferencesHydrated(false);

    axios
      .post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-favourites/get`,
        { currentSession },
        { withCredentials: true },
      )
      .then((response) => {
        if (!active) return;

        const data = response?.data?.data || {};
        if (data.exists) {
          const savedFavourites = Array.isArray(data.favourites)
            ? data.favourites
            : [];
          const savedCategories =
            Array.isArray(data.categories) &&
            data.categories.some((category) => category?.key === "other")
              ? data.categories
              : FAVOURITE_CATEGORIES;
          serverPreferencesExistRef.current = true;
          initializedRef.current = true;
          favouritesRef.current = savedFavourites;
          categoriesRef.current = savedCategories;
          setFavourites(savedFavourites);
          setCategories(savedCategories);
          setPreferencesHydrated(true);
          return;
        }

        const legacyFavourites = readStoredFavourites();
        const legacyCategories = readStoredFavouriteCategories();
        if (legacyFavourites !== null) {
          initializedRef.current = true;
          favouritesRef.current = legacyFavourites;
          categoriesRef.current = legacyCategories;
          setFavourites(legacyFavourites);
          setCategories(legacyCategories);
          setPreferencesHydrated(true);
          persistPreferences(legacyFavourites, legacyCategories, () => {
            try {
              window.localStorage.removeItem(FAVOURITES_STORAGE_KEY);
              window.localStorage.removeItem(FAVOURITE_CATEGORIES_STORAGE_KEY);
            } catch {
              // The database is now the source of truth even if cleanup is blocked.
            }
          });
        } else {
          categoriesRef.current = legacyCategories;
          setCategories(legacyCategories);
          setPreferencesHydrated(true);
        }
      })
      .catch(() => {
        if (!active) return;
        initializedRef.current = true;
        setPreferencesHydrated(true);
        onNotifyRef.current?.(
          "error",
          "Your saved favourites could not be loaded.",
        );
      });

    return () => {
      active = false;
    };
  }, [currentSession, persistPreferences]);

  useEffect(() => {
    if (
      initializedRef.current ||
      serverPreferencesExistRef.current ||
      !preferencesHydrated ||
      availableFields.length === 0
    ) {
      return;
    }

    initializedRef.current = true;
    const defaultFavourites = createDefaultFavourites(availableFields);
    commitPreferences(defaultFavourites, categoriesRef.current);
  }, [availableFields, commitPreferences, preferencesHydrated]);

  useEffect(() => {
    const activeCategories = new Set(
      automaticCategoryKey ? automaticCategoryKey.split("|") : [],
    );

    const previousCategories = previousAutomaticCategoriesRef.current;

    const newlyActivated = [...activeCategories].filter(
      (category) => !previousCategories.has(category),
    );

    const deactivated = [...previousCategories].filter(
      (category) => !activeCategories.has(category),
    );

    if (newlyActivated.length > 0) {
      setExpanded(true);
    }

    if (newlyActivated.length > 0 || deactivated.length > 0) {
      setExpandedCategories((previous) => {
        const next = new Set(previous);

        deactivated.forEach((category) => next.delete(category));
        newlyActivated.forEach((category) => next.add(category));

        return next;
      });
    }

    previousAutomaticCategoriesRef.current = activeCategories;
  }, [automaticCategoryKey]);

  const categoryMap = useMemo(
    () =>
      categories.reduce((result, category) => {
        result[category.key] = favourites.filter(
          (favourite) => favourite.category === category.key,
        );
        return result;
      }, {}),
    [favourites, categories],
  );

  const resolveField = (fieldId) =>
    availableFields.find(
      (field) => String(getFieldId(field)) === String(fieldId),
    );

  const handleDraftFieldChange = (fieldId) => {
    const field = resolveField(fieldId);
    setDraftFavourite((previous) => ({
      ...previous,
      fieldId,
      label: field ? getFieldLabel(field) : previous.label,
    }));
  };

  const handleAddFavourite = () => {
    const field = resolveField(draftFavourite.fieldId);
    if (!field) return;

    const previous = favouritesRef.current;
    const alreadyExists = previous.some(
      (item) =>
        String(item.fieldId) === String(draftFavourite.fieldId) &&
        item.category === draftFavourite.category,
    );

    if (alreadyExists) return;

    const nextFavourites = [
      ...previous,
      {
        ...draftFavourite,
        id: `favourite-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        label: draftFavourite.label.trim() || getFieldLabel(field),
      },
    ];
    commitPreferences(nextFavourites, categoriesRef.current);

    setExpanded(true);
    setExpandedCategories((previous) =>
      new Set(previous).add(draftFavourite.category),
    );

    setDraftFavourite((previous) => ({
      ...previous,
      fieldId: "",
      label: "",
    }));
  };

  const handleFavouriteChange = (id, key, value) => {
    const nextFavourites = favouritesRef.current.map((favourite) =>
      favourite.id === id ? { ...favourite, [key]: value } : favourite,
    );
    commitPreferences(nextFavourites, categoriesRef.current);
  };

  const handleRemoveFavourite = (id) => {
    const nextFavourites = favouritesRef.current.filter(
      (favourite) => favourite.id !== id,
    );
    favouritesRef.current = nextFavourites;
    setFavourites(nextFavourites);
    persistFavouriteDeletion(id);
  };

  const handleDrop = (targetFavourite, sourceId = draggedFavouriteId) => {
    if (loading || !sourceId || sourceId === targetFavourite.id) return;

    const previous = favouritesRef.current;
    const sourceIndex = previous.findIndex((item) => item.id === sourceId);

    const targetIndex = previous.findIndex(
      (item) => item.id === targetFavourite.id,
    );

    if (sourceIndex < 0 || targetIndex < 0) return;

    if (previous[sourceIndex].category !== previous[targetIndex].category) {
      return;
    }

    const next = [...previous];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);

    commitPreferences(next, categoriesRef.current);

    setDraggedFavouriteId(null);
  };

  const handleSelectFavourite = (favourite) => {
    const field = resolveField(favourite.fieldId);
    if (field) onFavouriteSelect?.(field, favourite);
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(categoryKey)) next.delete(categoryKey);
      else next.add(categoryKey);
      return next;
    });
  };

  const populatedCategories = categories.filter(
    (category) => (categoryMap[category.key]?.length ?? 0) > 0,
  );

  const handleAddCategory = () => {
    const label = newCategoryLabel.trim();

    if (!label) {
      setCategoryError("Enter a category name.");
      return;
    }

    const alreadyExists = categories.some(
      (category) => normalizeText(category.label) === normalizeText(label),
    );

    if (alreadyExists) {
      setCategoryError("This category already exists.");
      return;
    }

    const key = `custom-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    const nextCategories = [
      ...categoriesRef.current,
      { key, label, color: newCategoryColor },
    ];
    commitPreferences(favouritesRef.current, nextCategories);

    setExpandedCategories((previous) => new Set(previous).add(key));

    setDraftFavourite((previous) => ({
      ...previous,
      category: key,
    }));

    setNewCategoryLabel("");
    setNewCategoryColor("#64748b");
    setCategoryError("");
  };

  const handleRemoveCategory = (categoryKey) => {
    if (categoryKey === "other") return;

    // Preserve shortcuts by moving them into Other.
    const nextFavourites = favouritesRef.current.map((favourite) =>
        favourite.category === categoryKey
          ? { ...favourite, category: "other" }
          : favourite,
    );
    const nextCategories = categoriesRef.current.filter(
      (category) => category.key !== categoryKey,
    );
    commitPreferences(nextFavourites, nextCategories);

    setExpandedCategories((previous) => {
      const next = new Set(previous);
      next.delete(categoryKey);
      next.add("other");
      return next;
    });

    setDraftFavourite((previous) =>
      previous.category === categoryKey
        ? { ...previous, category: "other" }
        : previous,
    );
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
        disableGutters
        elevation={0}
        sx={{
          borderTop: "1px solid #e0e0e0",
          borderRadius: "0 !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary
          component="div"
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            minHeight: 42,
            px: 2,
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
              my: 0.75,
            },
          }}
        >
          <StarOutlineIcon sx={{ fontSize: 18, color: "warning.main" }} />
          <Typography variant="body2" fontWeight={700}>
            Favourites
          </Typography>
          <Chip label={favourites.length} size="small" sx={{ height: 20 }} />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
            onClick={(event) => {
              event.stopPropagation();
              setCustomizeOpen(true);
            }}
            onFocus={(event) => event.stopPropagation()}
            sx={{ textTransform: "none", py: 0.25 }}
          >
            Customize
          </Button>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            px: 2,
            pt: 0.5,
            pb: 1.5,
            maxHeight: "min(150px, 25vh)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {populatedCategories.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Add commonly used form fields to create your shortcuts.
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCustomizeOpen(true)}
                sx={{ mt: 0.5, textTransform: "none" }}
              >
                Add favourite
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridAutoFlow: "column",
                gridAutoColumns: "minmax(225px, 1fr)",
                gap: 1,
                minWidth: 0,
                overflowX: "auto",
                pb: 1,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: 3,
                },
              }}
            >
              {populatedCategories.map((category) => {
                const categoryFavourites = categoryMap[category.key];
                const categoryExpanded = expandedCategories.has(category.key);
                const isAutomatic = automaticCategories.includes(category.key);

                return (
                  <Accordion
                    key={category.key}
                    expanded={categoryExpanded}
                    onChange={() => toggleCategory(category.key)}
                    disableGutters
                    elevation={0}
                    sx={{
                      alignSelf: "start",
                      minWidth: 0,
                      border: "1px solid #dbe3ec",
                      borderLeft: `3px solid ${category.color}`,
                      borderRadius: "8px !important",
                      overflow: "hidden",
                      boxShadow: isAutomatic
                        ? `0 0 0 2px ${category.color}22`
                        : "none",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        minHeight: 34,
                        px: 1,
                        "&.Mui-expanded": { minHeight: 34 },
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 0.5,
                          my: 0.5,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          my: 0.5,
                        },
                      }}
                    >
                      <Typography variant="caption" fontWeight={700}>
                        {category.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {categoryFavourites.length}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
                      {categoryFavourites.length === 0 && (
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            No favourites added to this category yet.
                          </Typography>

                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              setDraftFavourite((previous) => ({
                                ...previous,
                                category: category.key,
                              }));
                              setCustomizeOpen(true);
                            }}
                            sx={{
                              display: "flex",
                              mt: 0.5,
                              textTransform: "none",
                            }}
                          >
                            Add favourite
                          </Button>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        {categoryFavourites.map((favourite) => (
                          <FavouriteDragItem
                            key={favourite.id}
                            favourite={favourite}
                            label={
                              resolveField(favourite.fieldId)
                                ? getFieldLabel(resolveField(favourite.fieldId))
                                : favourite.label
                            }
                            disabled={
                              loading || !resolveField(favourite.fieldId)
                            }
                            onReorder={handleDrop}
                          >
                            <Tooltip
                              title={
                                resolveField(favourite.fieldId)
                                  ? getFieldLabel(
                                      resolveField(favourite.fieldId),
                                    )
                                  : `${favourite.label} — field unavailable`
                              }
                              arrow
                              placement="top"
                              enterDelay={250}
                              disableInteractive
                            >
                              <span style={{ display: "inline-flex" }}>
                                <IconButton
                                  size="small"
                                  disabled={
                                    loading || !resolveField(favourite.fieldId)
                                  }
                                  aria-label={
                                    resolveField(favourite.fieldId)
                                      ? getFieldLabel(
                                          resolveField(favourite.fieldId),
                                        )
                                      : favourite.label
                                  }
                                  onClick={() =>
                                    handleSelectFavourite(favourite)
                                  }
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    border: "1px solid #d0d7de",
                                    borderRadius: 1.5,
                                    color: category.color,
                                    cursor: loading ? "default" : "grab",
                                    "&:hover": {
                                      backgroundColor: `${category.color}12`,
                                      borderColor: category.color,
                                    },
                                    "&:active": {
                                      cursor: "grabbing",
                                    },
                                  }}
                                >
                                  {FAVOURITE_ICON_OPTIONS[favourite.icon]
                                    ?.icon ??
                                    FAVOURITE_ICON_OPTIONS.description.icon}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </FavouriteDragItem>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StarOutlineIcon color="warning" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Customize favourites
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add shortcuts, choose their icon and category, or drag icons in
              the favourites bar to reorder them.
            </Typography>
          </Box>
          <IconButton onClick={() => setCustomizeOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          onScroll={() => {
            if (autocompleteOpen) {
              setAutocompleteOpen(false);
              autocompleteInputRef.current?.blur();
            }
          }}
          sx={{ overflow: "auto" }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Categories
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <TextField
              size="small"
              fullWidth
              label="New category"
              placeholder="For example, Private Provider"
              value={newCategoryLabel}
              error={Boolean(categoryError)}
              helperText={categoryError}
              onChange={(event) => {
                setNewCategoryLabel(event.target.value);
                setCategoryError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddCategory();
                }
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <TextField
              select
              size="small"
              label="Category colour"
              value={newCategoryColor}
              onChange={(event) => setNewCategoryColor(event.target.value)}
              sx={{ width: 150, flexShrink: 0 }}
            >
              {CATEGORY_COLOR_OPTIONS.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: value,
                        flexShrink: 0,
                      }}
                    />
                    {label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
              sx={{
                height: 50,
                minWidth: 140,
                px: 2,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              Add category
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.key}
                label={category.label}
                variant="outlined"
                onDelete={
                  category.key === "other"
                    ? undefined
                    : () => handleRemoveCategory(category.key)
                }
              />
            ))}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Removing a category moves its favourites to Other.
          </Typography>

          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Add a favourite
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(180px, 1.4fr) minmax(150px, 1fr) minmax(160px, 1fr) auto",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <Autocomplete
              size="small"
              fullWidth
              open={autocompleteOpen}
              onOpen={() => setAutocompleteOpen(true)}
              onClose={() => setAutocompleteOpen(false)}
              options={availableFields}
              value={resolveField(draftFavourite.fieldId) ?? null}
              getOptionLabel={getFavouriteOptionLabel}
              isOptionEqualToValue={(option, value) =>
                String(getFieldId(option)) === String(getFieldId(value))
              }
              onChange={(_, field) =>
                handleDraftFieldChange(field ? getFieldId(field) : "")
              }
              renderOption={(props, field) => (
                <li {...props} key={String(getFieldId(field))}>
                  {getFavouriteOptionLabel(field)}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={autocompleteInputRef}
                  label="Form field"
                  placeholder="Search fields"
                />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={draftFavourite.category}
                label="Category"
                onChange={(event) =>
                  setDraftFavourite((previous) => ({
                    ...previous,
                    category: event.target.value,
                  }))
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category.key} value={category.key}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select
                value={draftFavourite.icon}
                label="Icon"
                onChange={(event) =>
                  setDraftFavourite((previous) => ({
                    ...previous,
                    icon: event.target.value,
                  }))
                }
              >
                {Object.entries(FAVOURITE_ICON_OPTIONS).map(([key, option]) => (
                  <MenuItem key={key} value={key}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {option.icon}
                      <span>{option.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!draftFavourite.fieldId}
              onClick={handleAddFavourite}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Add
            </Button>
          </Box>
          <TextField
            value={draftFavourite.label}
            label="Custom tooltip label (optional)"
            size="small"
            fullWidth
            onChange={(event) =>
              setDraftFavourite((previous) => ({
                ...previous,
                label: event.target.value,
              }))
            }
            sx={{ mt: 1 }}
          />

          <Divider sx={{ my: 2.5 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Current favourites
          </Typography>
          {favourites.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No favourites have been added yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {favourites.map((favourite) => (
                <Box
                  key={favourite.id}
                  onDragOver={(event) => {
                    if (draggedFavouriteId) event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleDrop(favourite);
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "auto 1fr auto",
                      sm: "auto minmax(160px, 1.5fr) minmax(150px, 1fr) minmax(145px, 1fr) auto",
                    },
                    gap: 1,
                    alignItems: "center",
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Box
                    component="span"
                    draggable
                    title="Drag to reorder within this category"
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", favourite.id);
                      setDraggedFavouriteId(favourite.id);
                    }}
                    onDragEnd={() => setDraggedFavouriteId(null)}
                    sx={{ display: "inline-flex", cursor: "grab" }}
                  >
                    <DragIndicatorIcon color="action" />
                  </Box>
                  <TextField
                    value={favourite.label}
                    label="Tooltip label"
                    size="small"
                    onChange={(event) =>
                      handleFavouriteChange(
                        favourite.id,
                        "label",
                        event.target.value,
                      )
                    }
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={favourite.category}
                      label="Category"
                      onChange={(event) =>
                        handleFavouriteChange(
                          favourite.id,
                          "category",
                          event.target.value,
                        )
                      }
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.key} value={category.key}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Icon</InputLabel>
                    <Select
                      value={favourite.icon}
                      label="Icon"
                      onChange={(event) =>
                        handleFavouriteChange(
                          favourite.id,
                          "icon",
                          event.target.value,
                        )
                      }
                    >
                      {Object.entries(FAVOURITE_ICON_OPTIONS).map(
                        ([key, option]) => (
                          <MenuItem key={key} value={key}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              {option.icon}
                              <span>{option.label}</span>
                            </Stack>
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                  <Tooltip title="Remove favourite">
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveFavourite(favourite.id)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => setCustomizeOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FavouritesBar;
